import { API_ENDPOINTS, DEFAULT_PAGE_SIZE, ENTITY_STATUS } from '@/constants';
import { apiRequest } from '@/services/client';
import { validateStoreResponse } from '@/services/shared/contractValidation';
import {
  downloadEndpointFile,
  uploadSingleFile,
} from '@/services/shared/fileTransfer';
import { mapApiListResponse } from '@/services/shared/listResponse';
import type { ApiListResponse } from '@/types/api';
import type {
  Store,
  StoreListParams,
  StoreListResult,
  StorePayload,
} from '@/types/store';
import { normalizeUiPageFromParams } from '@/utils/pagination';

/**
 * Store list API response type
 *
 * Supports both legacy array format and standardized ApiListResponse format.
 * New endpoints should use ApiListResponse<Store> directly.
 *
 * @see {@link ApiListResponse} for the standardized format
 */
type StoreListApiResponse = unknown[] | ApiListResponse<unknown>; // Standardized format

/**
 * Maps API response to standardized StoreListResult format
 *
 * Handles both legacy array format and standardized ApiListResponse format.
 * Backend pagination is 0-based; UI pagination is 1-based.
 */
const mapStoreListResponse = (
  response: StoreListApiResponse,
  params: StoreListParams,
): StoreListResult => {
  return mapApiListResponse<unknown, Store>(response, params, {
    defaultPageSize: DEFAULT_PAGE_SIZE,
    mapItem: (item, index) =>
      validateStoreResponse(item, {
        operation: 'fetch_stores_list_item',
        endpoint: API_ENDPOINTS.STORES.LIST,
        itemIndex: index,
      }),
  });
};

const normalizeStatusParam = (status?: ENTITY_STATUS | ENTITY_STATUS[]) => {
  if (!status) {
    return undefined;
  }
  return Array.isArray(status) ? status.join(',') : status;
};

export const fetchStores = async (
  params: StoreListParams = {},
): Promise<StoreListResult> => {
  const normalizedUiPage = normalizeUiPageFromParams(params);

  const nameParam = params.name;

  const requestParams: Record<string, unknown> = {
    // Backend expects page request as 1-based, but responds with page as 0-based.
    page: normalizedUiPage,
    size: params.size,
    status: normalizeStatusParam(params.status),
  };

  if (nameParam && nameParam.trim() !== '') {
    requestParams.name = nameParam;
  }

  const response = await apiRequest<StoreListApiResponse>(
    API_ENDPOINTS.STORES.LIST,
    {
      params: requestParams,
      retry: { retries: 1 },
    },
  );

  return mapStoreListResponse(response, params);
};

export const createStore = async (payload: StorePayload): Promise<Store> => {
  const response = await apiRequest<unknown>(API_ENDPOINTS.STORES.CREATE, {
    method: 'POST',
    data: payload,
    retry: { retries: 0 },
  });
  return validateStoreResponse(response, {
    operation: 'create_store',
    endpoint: API_ENDPOINTS.STORES.CREATE,
  });
};

export const updateStore = async (
  id: number,
  payload: StorePayload,
): Promise<Store> => {
  const response = await apiRequest<unknown>(API_ENDPOINTS.STORES.UPDATE, {
    method: 'PUT',
    data: { id, ...payload },
    retry: { retries: 0 },
  });
  return validateStoreResponse(response, {
    operation: 'update_store',
    endpoint: API_ENDPOINTS.STORES.UPDATE,
  });
};

export const deleteStore = async (id: number): Promise<void> => {
  return apiRequest<void>(API_ENDPOINTS.STORES.DELETE(id), {
    method: 'DELETE',
    retry: { retries: 0 },
  });
};

export const bulkDeleteStores = async (ids: number[]): Promise<void> => {
  return apiRequest<void>(API_ENDPOINTS.STORES.BULK_DELETE, {
    method: 'DELETE',
    data: ids,
    retry: { retries: 0 },
  });
};

export const bulkReactivateStores = async (ids: number[]): Promise<void> => {
  return apiRequest<void>(API_ENDPOINTS.STORES.BULK_REACTIVATE, {
    method: 'PATCH',
    data: ids,
    retry: { retries: 0 },
  });
};

export const reactivateStore = async (id: number): Promise<Store> => {
  const response = await apiRequest<unknown>(
    API_ENDPOINTS.STORES.REACTIVATE(id),
    {
      method: 'PATCH',
      retry: { retries: 0 },
    },
  );
  return validateStoreResponse(response, {
    operation: 'reactivate_store',
    endpoint: API_ENDPOINTS.STORES.REACTIVATE(id),
  });
};

export const downloadStores = async (): Promise<{
  blob: Blob;
  filename: string;
}> => {
  return downloadEndpointFile(API_ENDPOINTS.STORES.EXPORT, 'store.xlsx');
};

// Upload an XLSX file to create store in bulk.
// Backend expects: multipart/form-data with a single part named "file" (xlsx only).
// It will process asynchronously and send a report via email to the current user.
export const uploadStoresFile = async (
  file: File,
): Promise<{ jobId?: string; message?: string }> => {
  return uploadSingleFile(API_ENDPOINTS.STORES.UPLOAD, file);
};
